import {
  Device,
  USER_ROLES_IDS,
  UserAppointment,
} from "../../constants/common";
import DeviceDetailsTable from "../table/DeviceDetailsTable";
import AddDeviceDetailsCard from "./AddDeviceDetailsCard";
import Accordion from "../accordian";
import React, { Dispatch, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../button";
import { Plus } from "lucide-react";
import useAuthStore from "../../store/Auth";
import { useLocation } from "react-router-dom";
import { APP_ROUTES } from "../../routes";
import CommentsDrawer from "../drawer/CommentsDrawer";

interface DeviceDetailsCardProps {
  clearErrors: any;
  userAppointmentDetails?: UserAppointment;
  addedDevices: any;
  setAddedDevices: Dispatch<any>;
  cardNumber?: number;
  isSubmitDisabled?: boolean;
  nextApproverRoleId?: USER_ROLES_IDS;
}

const DeviceDetailsCard = ({
  clearErrors,
  userAppointmentDetails,
  addedDevices,
  setAddedDevices,
  cardNumber = 4,
  isSubmitDisabled,
  nextApproverRoleId,
}: DeviceDetailsCardProps) => {
  const location = useLocation();

  const [isDeviceDeleted, setIsDeviceDeleted] = useState<boolean>(false);

  const [selectedKey, setSelectedKey] = useState<string>("1");
  const [isAddVisitorFormVisible, setIsAddVisitorFormVisible] =
    useState<boolean>(
      location.pathname !== APP_ROUTES.APPROVE_REQUEST &&
        location.pathname !== APP_ROUTES.UPDATE_REQUEST
    );

  const user = useAuthStore((state) => state.user);

  const { t } = useTranslation();

  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);

  const isCreateVisitRequest = useMemo(
    () =>
      location.pathname === APP_ROUTES.INDIVIDUAL_VISIT_REQUEST ||
      location.pathname === APP_ROUTES.VIP_VISIT_REQUEST ||
      location.pathname === APP_ROUTES.GROUP_VISIT_REQUEST,
    [location.pathname]
  );

  const isUpdateVisitRequest = useMemo(
    () => location.pathname === APP_ROUTES.UPDATE_REQUEST,
    [location.pathname]
  );

  const onDeviceEdit = useCallback(
    (id: string) => {
      const selectedDevice = addedDevices?.find(
        (item: any) =>
          item.id === id || item.appointmentDeviceId?.toString() === id
      );
      setSelectedDevice(selectedDevice);
      setIsAddVisitorFormVisible(true);
    },
    [addedDevices]
  );

  const isUserAppointmentApprover = useMemo(
    () => nextApproverRoleId === user?.n_RoleID,
    [nextApproverRoleId, user?.n_RoleID]
  );

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordion
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 items-end -mt-2">
            {t(`${cardNumber}`)}. {t("deviceDetails")}
          </h1>
        }
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-end">
            {(location.pathname === APP_ROUTES.APPROVE_REQUEST ||
              (location.pathname === APP_ROUTES.UPDATE_REQUEST &&
                user?.n_RoleID === USER_ROLES_IDS.STAFF)) && (
              <CommentsDrawer noButtonStyles />
            )}

            {!isSubmitDisabled &&
              (isCreateVisitRequest ||
                isUpdateVisitRequest ||
                isUserAppointmentApprover) && (
                <Button
                  type="button"
                  onClick={() => {
                    setIsAddVisitorFormVisible(true);
                    setSelectedDevice(null);
                  }}
                  Icon={<Plus size={20} />}
                >
                  {t("addDevice")}
                </Button>
              )}
          </div>

          <DeviceDetailsTable
            addedDevices={addedDevices}
            setAddedDevices={setAddedDevices}
            userAppointmentDetails={userAppointmentDetails}
            onDeviceEdit={onDeviceEdit}
            isSubmitDisabled={isSubmitDisabled}
            isUserAppointmentApprover={isUserAppointmentApprover}
            approvalStatus={userAppointmentDetails?.approvalStatus}
            setIsDeviceDeleted={setIsDeviceDeleted}
          />

          {!isSubmitDisabled &&
            (isCreateVisitRequest ||
              isUpdateVisitRequest ||
              isUserAppointmentApprover) &&
            isAddVisitorFormVisible && (
              <AddDeviceDetailsCard
                clearErrors={clearErrors}
                userAppointmentDetails={userAppointmentDetails}
                setAddedDevices={setAddedDevices}
                setIsAddVisitorFormVisible={setIsAddVisitorFormVisible}
                selectedDevice={selectedDevice}
                setSelectedDevice={setSelectedDevice}
                isDeviceDeleted={isDeviceDeleted}
                setIsDeviceDeleted={setIsDeviceDeleted}
              />
            )}
        </div>
      </Accordion>
    </div>
  );
};

export default React.memo(DeviceDetailsCard);
